import type {
  AppState,
  Content,
  Goal,
  RoutineEvent,
  ScoreLog,
  Session,
  Settings,
} from '@app-types/domain';
import { INITIAL_STATE } from '@app-types/domain';
import { todayLocalIso } from './dates';
import { type TrackedEventType, appendEvent, makeEvent } from './events';
import { loadState, saveState } from './persistence';
import { type ReviewQuality, answerReview, initialReview } from './sm2';

type Listener = () => void;

const state = { current: loadState() };
const listeners = new Set<Listener>();

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => saveState(state.current), 250);
}

function set(updater: (s: AppState) => AppState): void {
  state.current = updater(state.current);
  listeners.forEach((l) => l());
  scheduleSave();
}

export function getState(): AppState {
  return state.current;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** substitui o estado inteiro (usado pelo sync ao aplicar dado remoto) */
export function replaceState(next: AppState): void {
  set(() => ({
    ...INITIAL_STATE,
    ...next,
    settings: { ...INITIAL_STATE.settings, ...(next.settings || {}) },
  }));
}

// ===== Helpers =====
function nowISO() {
  return new Date().toISOString();
}
function newId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** registra um evento no log dentro de um updater (imutável, com cap) */
function track(s: AppState, type: TrackedEventType, payload: Record<string, unknown>): AppState {
  return { ...s, events: appendEvent(s.events, makeEvent(type, payload)) };
}

// ===== Actions: Contents =====
export const contentActions = {
  add(input: Omit<Content, 'id' | 'created_at' | 'updated_at' | 'order'>): Content {
    const c: Content = {
      ...input,
      id: newId('cnt'),
      order: state.current.contents.length,
      created_at: nowISO(),
      updated_at: nowISO(),
    };
    set((s) => ({ ...s, contents: [...s.contents, c] }));
    return c;
  },
  update(id: string, patch: Partial<Omit<Content, 'id' | 'created_at'>>): void {
    set((s) => ({
      ...s,
      contents: s.contents.map((c) => {
        if (c.id !== id) return c;
        const next = { ...c, ...patch, updated_at: nowISO() };
        // concluiu → entra na rotação de revisão espaçada (1ª revisão amanhã)
        if (patch.status === 'done' && !next.review) next.review = initialReview(todayLocalIso());
        return next;
      }),
    }));
  },
  remove(id: string): void {
    set((s) => ({
      ...s,
      contents: s.contents.filter((c) => c.id !== id),
      sessions: s.sessions.filter((sess) => sess.content_id !== id),
    }));
  },
  bulkAdd(items: Omit<Content, 'id' | 'created_at' | 'updated_at' | 'order'>[]): number {
    let added = 0;
    set((s) => {
      const base = s.contents.length;
      const newItems: Content[] = items.map((item, i) => ({
        ...item,
        id: newId('cnt'),
        order: base + i,
        created_at: nowISO(),
        updated_at: nowISO(),
      }));
      added = newItems.length;
      return { ...s, contents: [...s.contents, ...newItems] };
    });
    return added;
  },
  clearAll(): void {
    set((s) => ({ ...s, contents: [], sessions: [] }));
  },
};

// ===== Actions: Sessions =====
export const sessionActions = {
  create(
    content_id: string,
    planned_at: string,
    planned_duration_min: number,
    goal_id?: string,
  ): Session {
    const sess: Session = {
      id: newId('ses'),
      content_id,
      goal_id,
      planned_at,
      planned_duration_min,
      status: 'planned',
    };
    set((s) => ({ ...s, sessions: [...s.sessions, sess] }));
    return sess;
  },
  start(id: string): void {
    set((s) => {
      const target = s.sessions.find((x) => x.id === id);
      const next = {
        ...s,
        sessions: s.sessions.map((sess) =>
          sess.id === id ? { ...sess, status: 'active' as const, started_at: nowISO() } : sess,
        ),
      };
      return target
        ? track(next, 'session.started', {
            session_id: id,
            content_id: target.content_id,
            goal_id: target.goal_id,
            planned_duration_min: target.planned_duration_min,
          })
        : next;
    });
  },
  complete(id: string, actual_duration_min: number, focus_score?: number, notes?: string): void {
    set((s) => {
      const target = s.sessions.find((x) => x.id === id);
      const sessions = s.sessions.map((sess) =>
        sess.id === id
          ? {
              ...sess,
              status: 'completed' as const,
              completed_at: nowISO(),
              actual_duration_min,
              focus_score,
              notes,
            }
          : sess,
      );
      // conteúdo só vira 'done' quando não sobra sessão pendente dele
      const contents = s.contents.map((c) => {
        if (!target || c.id !== target.content_id) return c;
        const pendingLeft = sessions.some(
          (x) => x.content_id === c.id && (x.status === 'planned' || x.status === 'active'),
        );
        return pendingLeft
          ? { ...c, status: 'doing' as const, updated_at: nowISO() }
          : {
              ...c,
              status: 'done' as const,
              review: c.review ?? initialReview(todayLocalIso()),
              updated_at: nowISO(),
            };
      });
      const next = { ...s, sessions, contents };
      return target
        ? track(next, 'session.completed', {
            session_id: id,
            content_id: target.content_id,
            goal_id: target.goal_id,
            planned_duration_min: target.planned_duration_min,
            actual_duration_min,
            focus_score,
          })
        : next;
    });
  },
  abandon(id: string): void {
    set((s) => {
      const target = s.sessions.find((x) => x.id === id);
      const next = {
        ...s,
        sessions: s.sessions.map((sess) =>
          sess.id === id ? { ...sess, status: 'abandoned' as const, completed_at: nowISO() } : sess,
        ),
      };
      return target
        ? track(next, 'session.abandoned', { session_id: id, content_id: target.content_id })
        : next;
    });
  },
  remove(id: string): void {
    set((s) => ({ ...s, sessions: s.sessions.filter((sess) => sess.id !== id) }));
  },
  /** cria várias sessões de uma vez (planner) */
  bulkCreate(
    drafts: {
      content_id: string;
      goal_id?: string;
      planned_at: string;
      planned_duration_min: number;
    }[],
  ): number {
    let added = 0;
    set((s) => {
      const newSessions: Session[] = drafts.map((d) => ({
        id: newId('ses'),
        content_id: d.content_id,
        goal_id: d.goal_id,
        planned_at: d.planned_at,
        planned_duration_min: d.planned_duration_min,
        status: 'planned' as const,
      }));
      added = newSessions.length;
      const next = { ...s, sessions: [...s.sessions, ...newSessions] };
      return newSessions.length > 0
        ? track(next, 'plan.generated', {
            sessions: newSessions.length,
            first_at: newSessions[0].planned_at,
            last_at: newSessions[newSessions.length - 1].planned_at,
          })
        : next;
    });
    return added;
  },
  /** remove sessões futuras não iniciadas (para regenerar plano sem perder histórico) */
  clearPlanned(): number {
    let removed = 0;
    set((s) => {
      const keep = s.sessions.filter((x) => x.status !== 'planned');
      removed = s.sessions.length - keep.length;
      return { ...s, sessions: keep };
    });
    return removed;
  },
  /** muda o horário planejado (drag na Agenda) */
  reschedule(id: string, planned_at: string): void {
    set((s) => ({
      ...s,
      sessions: s.sessions.map((sess) => (sess.id === id ? { ...sess, planned_at } : sess)),
    }));
  },
  /** aplica focus_score retroativo a várias sessões (prompt de fim de ciclo) */
  setFocus(ids: string[], focus_score: number): void {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    set((s) =>
      track(
        {
          ...s,
          sessions: s.sessions.map((sess) =>
            idSet.has(sess.id) ? { ...sess, focus_score } : sess,
          ),
        },
        'focus.reported',
        { session_ids: ids, score: focus_score },
      ),
    );
  },
};

// ===== Actions: Goals =====
export const goalActions = {
  add(input: Omit<Goal, 'id' | 'created_at' | 'updated_at'>): Goal {
    const g: Goal = {
      ...input,
      id: newId('goa'),
      created_at: nowISO(),
      updated_at: nowISO(),
    };
    set((s) =>
      track({ ...s, goals: [...s.goals, g] }, 'goal.created', {
        goal_id: g.id,
        type: g.type,
        area: g.area,
        parent_id: g.parent_id,
        target_date: g.target_date,
        target_hours: g.target_hours,
        has_value_target: g.target_value !== undefined,
      }),
    );
    return g;
  },
  update(id: string, patch: Partial<Omit<Goal, 'id' | 'created_at'>>): void {
    set((s) => {
      const before = s.goals.find((g) => g.id === id);
      const next = {
        ...s,
        goals: s.goals.map((g) => (g.id === id ? { ...g, ...patch, updated_at: nowISO() } : g)),
      };
      const achievedNow = before && before.status !== 'achieved' && patch.status === 'achieved';
      return achievedNow ? track(next, 'goal.completed', { goal_id: id }) : next;
    });
  },
  remove(id: string): void {
    set((s) => ({
      ...s,
      // submetas do grupo removido viram metas raiz (não somem junto)
      goals: s.goals
        .filter((g) => g.id !== id)
        .map((g) => (g.parent_id === id ? { ...g, parent_id: undefined } : g)),
      contents: s.contents.map((c) => (c.goalId === id ? { ...c, goalId: undefined } : c)),
    }));
  },
  /** check-in de meta numérica (peso, R$...) — gera evento goal.progress */
  checkIn(id: string, value: number): void {
    set((s) => {
      const g = s.goals.find((x) => x.id === id);
      if (!g) return s;
      const next = {
        ...s,
        goals: s.goals.map((x) =>
          x.id === id ? { ...x, current_value: value, updated_at: nowISO() } : x,
        ),
      };
      return track(next, 'goal.progress', {
        goal_id: id,
        value,
        start_value: g.start_value,
        target_value: g.target_value,
        unit: g.unit,
      });
    });
  },
  setActive(id: string | undefined): void {
    set((s) => ({ ...s, settings: { ...s.settings, active_goal_id: id } }));
  },
};

// ===== Actions: Routines =====
export const routineActions = {
  add(input: Omit<RoutineEvent, 'id' | 'created_at' | 'updated_at'>): RoutineEvent {
    const r: RoutineEvent = {
      ...input,
      id: newId('rot'),
      created_at: nowISO(),
      updated_at: nowISO(),
    };
    set((s) => ({ ...s, routines: [...s.routines, r] }));
    return r;
  },
  update(id: string, patch: Partial<Omit<RoutineEvent, 'id' | 'created_at'>>): void {
    set((s) => ({
      ...s,
      routines: s.routines.map((r) => (r.id === id ? { ...r, ...patch, updated_at: nowISO() } : r)),
    }));
  },
  remove(id: string): void {
    set((s) => ({ ...s, routines: s.routines.filter((r) => r.id !== id) }));
  },
};

// ===== Actions: Reviews (SM-2) =====
export const reviewActions = {
  answer(contentId: string, quality: ReviewQuality): void {
    set((s) => {
      const c = s.contents.find((x) => x.id === contentId);
      if (!c?.review) return s;
      const nextReview = answerReview(c.review, quality, todayLocalIso());
      const next = {
        ...s,
        contents: s.contents.map((x) =>
          x.id === contentId ? { ...x, review: nextReview, updated_at: nowISO() } : x,
        ),
      };
      return track(next, 'review.completed', {
        content_id: contentId,
        quality,
        reps: nextReview.reps,
        interval_days: nextReview.interval_days,
        next_due: nextReview.due_date,
      });
    });
  },
};

// ===== Actions: Settings =====
export const settingsActions = {
  update(patch: Partial<Settings>): void {
    set((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
  },
};

// ===== Actions: Score =====
export const scoreActions = {
  addLog(log: ScoreLog): void {
    set((s) => {
      const firstOfDay = !s.scoreLogs.some((x) => x.date === log.date);
      const next = {
        ...s,
        scoreLogs: [...s.scoreLogs.filter((x) => x.date !== log.date), log].sort((a, b) =>
          a.date.localeCompare(b.date),
        ),
      };
      // só o primeiro snapshot do dia vira evento (updates intra-dia não poluem o log)
      return firstOfDay
        ? track(next, 'score.snapshot', { date: log.date, overall: log.overall })
        : next;
    });
  },
};

// ===== Reset (careful) =====
export function resetAllState(): void {
  set(() => ({
    contents: [],
    sessions: [],
    plans: [],
    goals: [],
    routines: [],
    scoreLogs: [],
    events: [],
    settings: state.current.settings, // preserva settings
  }));
}
